import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Link as MuiLink,
  Switch,
  FormControlLabel,
  Pagination,
} from '@mui/material';
import { fetchItem } from '../../support/queries';

// Utility function to parse RSS XML data
const parseRSS = (xml) => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xml, 'application/xml');
  const items = Array.from(xmlDoc.querySelectorAll('item')).map((item) => ({
    title: item.querySelector('title')?.textContent || '',
    description: item.querySelector('description')?.textContent || '',
    link: item.querySelector('link')?.textContent || '',
    image: item.querySelector('enclosure')?.getAttribute('url') || '',
    pubDate: item.querySelector('pubDate')?.textContent || '',
  }));
  return items;
};

const formatDate = (pubDate) => {
  if (!pubDate) return '';
  const date = new Date(pubDate);
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const RssFeedComponent = () => {
  const [items, setItems] = useState([]);
  const [isTableView, setIsTableView] = useState(false); // Default to card view
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8; // 2 cards per row and 2 rows per page

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchItem('news/rss/', {
          feed_url: 'http://feeds.feedburner.com/TheHackersNews?format=xml',
        });
        const data = await response.feed;
        const parsedItems = parseRSS(data);
        setItems(parsedItems);
      } catch (error) {
        console.error('Error fetching RSS feed:', error);
      }
    };

    fetchData();
  }, []);

  const handleToggleChange = (event) => {
    setIsTableView(event.target.checked);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const paginatedItems = items.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className='rss-feed px-4'>
      <h2>The Hacker News</h2>

      <FormControlLabel
        control={<Switch checked={isTableView} onChange={handleToggleChange} />}
        label={isTableView ? 'Table View' : 'Card View'}
      />

      {isTableView ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Publication Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedItems.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <MuiLink
                      href={item.link}
                      target='_blank'
                      rel='noopener'
                      title={item.description}
                    >
                      {item.title}
                    </MuiLink>
                  </TableCell>
                  <TableCell>{formatDate(item.pubDate)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <div className='flex flex-wrap gap-4'>
          {paginatedItems.map((item, index) => (
            <Card key={index} className='flex-1 min-w-48 2xl:max-w-72'>
              <CardMedia
                component='img'
                height='100'
                image={item.image}
                alt={item.title}
              />
              <CardContent>
                <Typography variant='body2' color='text.secondary'>
                  {formatDate(item.pubDate)}
                </Typography>
                <Typography variant='body1' component='div' gutterBottom>
                  <MuiLink
                    href={item.link}
                    target='_blank'
                    rel='noopener'
                    title={item.description}
                  >
                    {item.title}
                  </MuiLink>
                </Typography>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Pagination
        count={Math.ceil(items.length / itemsPerPage)}
        page={currentPage}
        onChange={handlePageChange}
        color='primary'
        style={{ marginTop: '20px', justifyContent: 'center', display: 'flex' }}
      />
    </div>
  );
};

export default RssFeedComponent;
